import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import EmailBlockEditor from "../../components/admin/EmailBlockEditor";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Mail,
  Edit,
  Eye,
  RotateCcw,
  Search,
  FileText,
  Users,
  Shield,
  Globe,
  Loader2,
  Zap,
  Info,
  Code,
  Monitor,
  Save,
  Plus,
  Trash2,
  LayoutGrid,
  List,
  Send,
  ToggleLeft,
  Sparkles,
  Workflow,
  SendHorizonal,
  Tag,
  Repeat,
  Hand,
  Briefcase,
  Receipt,
  Clock,
  RefreshCw,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

// ─── Stat Card ─────────────────────────────────────────────
const StatCard = ({ label, value, gradient, icon: Icon }) => (
  <div className="flex items-center gap-4 bg-white rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-sm`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="font-accent text-xl font-bold text-rnb-navy">{value}</p>
      <p className="text-xs text-gray-400">{label}</p>
    </div>
  </div>
);

// ─── Category Pill ─────────────────────────────────────────
const CategoryPill = ({ label, icon: Icon, count, active, onClick, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
      active ? "bg-rnb-navy text-white shadow-sm" : "bg-white text-gray-500 hover:text-rnb-navy hover:shadow-sm"
    }`}
    data-testid={testId}
  >
    <Icon className="w-4 h-4" />
    {label}
    <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"}`}>{count}</span>
  </button>
);

// ─── Template Card (grid) ──────────────────────────────────
const TemplateCardGrid = ({ template, onView, onEdit, onToggle, onDelete, onSend, actionLoading }) => {
  const getCatColor = (c) => ({ program_applications: "from-blue-500 to-indigo-500", client_portal: "from-rnb-teal to-emerald-500", account: "from-amber-400 to-orange-500", admin: "from-rnb-navy to-rnb-blue", public: "from-violet-500 to-purple-500", custom: "from-pink-500 to-rose-500" }[c] || "from-gray-400 to-gray-500");
  const getCatIcon = (c) => ({ program_applications: FileText, client_portal: Users, account: Shield, admin: Mail, public: Globe, custom: Sparkles }[c] || Mail);
  const CatIcon = getCatIcon(template.category);

  return (
    <div className={`bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all group flex flex-col ${!template.is_active ? "opacity-50" : ""}`} data-testid={`template-${template.template_key}`}>
      <div className={`h-2 bg-gradient-to-r ${getCatColor(template.category)}`} />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getCatColor(template.category)} flex items-center justify-center`}>
            <CatIcon className="w-4.5 h-4.5 text-white" />
          </div>
          <Switch checked={template.is_active} onCheckedChange={() => onToggle(template.template_key)} disabled={actionLoading === template.template_key} data-testid={`toggle-${template.template_key}`} />
        </div>
        <div className="flex items-center gap-1.5 mb-1">
          <h3 className="text-sm font-semibold text-rnb-navy truncate">{template.name}</h3>
          {template.is_custom && <Badge className="text-[9px] px-1.5 py-0 bg-pink-50 text-pink-600 border border-pink-200 hover:bg-pink-50 shrink-0">Custom</Badge>}
        </div>
        <p className="text-xs text-gray-400 mb-3 line-clamp-2">{template.description || template.trigger}</p>
        <div className="bg-gray-50 rounded-lg px-3 py-2 mb-4 mt-auto">
          <p className="text-[11px] text-gray-400 mb-0.5">Subject</p>
          <p className="text-xs text-rnb-navy font-medium truncate">{template.subject}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onView(template)} className="flex-1 h-8 text-xs" data-testid={`view-${template.template_key}`}>
            <Eye className="h-3 w-3 mr-1" /> Preview
          </Button>
          <Button size="sm" onClick={() => onEdit(template)} className="flex-1 h-8 text-xs bg-rnb-navy hover:bg-rnb-navy/90" data-testid={`edit-${template.template_key}`}>
            <Edit className="h-3 w-3 mr-1" /> Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => onSend(template)} className="h-8 w-8 p-0 text-rnb-teal hover:text-rnb-teal/80 border-rnb-teal/30" data-testid={`send-${template.template_key}`} title="Send One-Off Email">
            <Send className="h-3.5 w-3.5" />
          </Button>
          {template.is_custom && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(template.template_key)} className="h-8 w-8 p-0 text-gray-300 hover:text-red-500" data-testid={`delete-${template.template_key}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Template Card (list) ──────────────────────────────────
const TemplateCardList = ({ template, onView, onEdit, onToggle, onDelete, onSend, actionLoading }) => {
  const getCatColor = (c) => ({ program_applications: "from-blue-500 to-indigo-500", client_portal: "from-rnb-teal to-emerald-500", account: "from-amber-400 to-orange-500", admin: "from-rnb-navy to-rnb-blue", public: "from-violet-500 to-purple-500", custom: "from-pink-500 to-rose-500" }[c] || "from-gray-400 to-gray-500");
  const getCatIcon = (c) => ({ program_applications: FileText, client_portal: Users, account: Shield, admin: Mail, public: Globe, custom: Sparkles }[c] || Mail);
  const CatIcon = getCatIcon(template.category);

  return (
    <div className={`bg-white rounded-xl px-5 py-4 flex items-center justify-between hover:shadow-md transition-all group ${!template.is_active ? "opacity-50" : ""}`} data-testid={`template-${template.template_key}`}>
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getCatColor(template.category)} flex items-center justify-center shrink-0`}>
          <CatIcon className="w-4.5 h-4.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-semibold text-rnb-navy truncate">{template.name}</p>
            {template.is_custom && <Badge className="text-[10px] px-1.5 py-0 bg-pink-50 text-pink-600 border border-pink-200 hover:bg-pink-50">Custom</Badge>}
            {!template.is_active && <Badge className="text-[10px] px-1.5 py-0 bg-red-50 text-red-500 border border-red-200 hover:bg-red-50">Off</Badge>}
          </div>
          <p className="text-xs text-gray-400 truncate">{template.trigger} &middot; {template.subject}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-4">
        <Switch checked={template.is_active} onCheckedChange={() => onToggle(template.template_key)} disabled={actionLoading === template.template_key} />
        <Button variant="ghost" size="sm" onClick={() => onSend(template)} className="h-8 w-8 p-0 text-rnb-teal hover:text-rnb-teal/80" title="Send One-Off Email"><Send className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => onView(template)} className="h-8 w-8 p-0 text-gray-400 hover:text-rnb-navy"><Eye className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => onEdit(template)} className="h-8 w-8 p-0 text-gray-400 hover:text-rnb-blue"><Edit className="h-4 w-4" /></Button>
        {template.is_custom && <Button variant="ghost" size="sm" onClick={() => onDelete(template.template_key)} className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────
const AdminEmailTemplates = () => {
  const { token, API } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("templates"); // "templates" | "automations"

  // Detail / Edit
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fullTemplate, setFullTemplate] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editHtmlContent, setEditHtmlContent] = useState("");
  const [editMode, setEditMode] = useState("preview");
  const previewRef = useRef(null);

  // Create template
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1); // 1=details, 2=design, 3=review
  const [newTemplate, setNewTemplate] = useState({ template_key: "", name: "", category: "custom", trigger: "", subject: "", description: "", html_content: "", variables: [] });
  const [newBlocks, setNewBlocks] = useState([]);
  const [blockPreviewHtml, setBlockPreviewHtml] = useState("");

  // Automations
  const [automations, setAutomations] = useState([]);
  const [triggerTypes, setTriggerTypes] = useState({});
  const [triggerCategories, setTriggerCategories] = useState({});
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [newAutomation, setNewAutomation] = useState({ name: "", template_key: "", trigger_type: "", offset_value: null, condition: "", recipient_type: "client", custom_recipient: "" });

  // Variables & Test Email
  const [variableRegistry, setVariableRegistry] = useState(null);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailTemplate, setTestEmailTemplate] = useState(null);

  // One-Off Send
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendTemplate, setSendTemplate] = useState(null);
  const [sendToEmail, setSendToEmail] = useState("");
  const [sendVariables, setSendVariables] = useState({});
  const [sendLoading, setSendLoading] = useState(false);

  // Recently Sent
  const [recentSends, setRecentSends] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, aRes, vRes, trRes] = await Promise.all([
        fetch(`${API}/admin/email-templates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/email-automations`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/email-templates/variables/registry`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/email-automations/trigger-types`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (tRes.ok) { const d = await tRes.json(); setTemplates(d.templates || []); setCategoryLabels(d.category_labels || {}); }
      if (aRes.ok) { const d = await aRes.json(); setAutomations(d.automations || []); }
      if (vRes.ok) { const d = await vRes.json(); setVariableRegistry(d.registry); }
      if (trRes.ok) { const d = await trRes.json(); setTriggerTypes(d.trigger_types || {}); setTriggerCategories(d.categories || {}); }
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const fetchRecentSends = async () => {
    setRecentLoading(true);
    try {
      const r = await fetch(`${API}/admin/email-automations/recent-sends?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setRecentSends(d.logs || []); }
    } catch (err) { console.error("Template fetch failed:", err); }
    finally { setRecentLoading(false); }
  };

  const fetchFullTemplate = async (key) => {
    try {
      const res = await fetch(`${API}/admin/email-templates/${key}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setFullTemplate(d); return d; }
    } catch (err) { console.error("Template fetch failed:", err); }
    return null;
  };

  // ─── Template Actions ─────────────────────────────────────
  const handleToggle = async (key) => {
    setActionLoading(key);
    try {
      const r = await fetch(`${API}/admin/email-templates/${key}/toggle`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); toast.success(d.message); fetchAll(); } else toast.error("Failed");
    } catch { toast.error("Failed"); } finally { setActionLoading(false); }
  };

  const handleReset = async (key) => {
    if (!confirm("Reset to default? This removes all customizations.")) return;
    setActionLoading(key);
    try {
      const r = await fetch(`${API}/admin/email-templates/${key}/reset`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Reset to default"); fetchAll(); setDetailModalOpen(false); setEditModalOpen(false); }
    } catch { toast.error("Failed"); } finally { setActionLoading(false); }
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    setActionLoading("update");
    try {
      const r = await fetch(`${API}/admin/email-templates/${selectedTemplate.template_key}`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ subject: editSubject, html_content: editHtmlContent || null }),
      });
      if (r.ok) { toast.success("Template saved"); fetchAll(); setEditModalOpen(false); } else { const e = await r.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed"); } finally { setActionLoading(false); }
  };

  const handleDeleteTemplate = async (key) => {
    if (!confirm("Delete this custom template?")) return;
    setActionLoading(key);
    try {
      const r = await fetch(`${API}/admin/email-templates/${key}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Deleted"); fetchAll(); }
    } catch { toast.error("Failed"); } finally { setActionLoading(false); }
  };

  // ─── Create Template with Block Editor ────────────────────
  const handlePreviewBlocks = async () => {
    try {
      const r = await fetch(`${API}/admin/email-templates/blocks/preview`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ blocks: newBlocks, subject: newTemplate.subject }),
      });
      if (r.ok) { const d = await r.json(); setBlockPreviewHtml(d.preview_html); setNewTemplate((t) => ({ ...t, html_content: d.html })); }
    } catch (err) { console.error("Template fetch failed:", err); }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.template_key || !newTemplate.name || !newTemplate.subject) { toast.error("Fill in required fields"); return; }
    // Generate HTML from blocks if any
    let htmlContent = newTemplate.html_content;
    if (newBlocks.length > 0) {
      try {
        const r = await fetch(`${API}/admin/email-templates/blocks/preview`, {
          method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ blocks: newBlocks, subject: newTemplate.subject }),
        });
        if (r.ok) { const d = await r.json(); htmlContent = d.html; }
      } catch (err) { console.error("Template fetch failed:", err); }
    }
    setActionLoading("create");
    try {
      const r = await fetch(`${API}/admin/email-templates`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ...newTemplate, html_content: htmlContent, template_key: newTemplate.template_key.toLowerCase().replace(/[^a-z0-9_]/g, "_") }),
      });
      if (r.ok) { toast.success("Template created"); fetchAll(); setCreateModalOpen(false); resetCreate(); } else { const e = await r.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed"); } finally { setActionLoading(false); }
  };

  const resetCreate = () => {
    setNewTemplate({ template_key: "", name: "", category: "custom", trigger: "", subject: "", description: "", html_content: "", variables: [] });
    setNewBlocks([]);
    setBlockPreviewHtml("");
    setCreateStep(1);
  };

  // ─── Automations ──────────────────────────────────────────
  const handleCreateAutomation = async () => {
    if (!newAutomation.name || !newAutomation.template_key || !newAutomation.trigger_type) { toast.error("Fill in required fields"); return; }
    setActionLoading("auto_create");
    try {
      const r = await fetch(`${API}/admin/email-automations`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(newAutomation),
      });
      if (r.ok) { toast.success("Automation created"); fetchAll(); setAutomationModalOpen(false); setNewAutomation({ name: "", template_key: "", trigger_type: "", offset_value: null, condition: "", recipient_type: "client", custom_recipient: "" }); }
      else { const e = await r.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed"); } finally { setActionLoading(false); }
  };

  const handleToggleAutomation = async (id) => {
    try {
      const r = await fetch(`${API}/admin/email-automations/${id}/toggle`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleDeleteAutomation = async (id) => {
    if (!confirm("Delete this automation?")) return;
    try {
      const r = await fetch(`${API}/admin/email-automations/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Deleted"); fetchAll(); }
    } catch { toast.error("Failed"); }
  };

  const handleRunNow = async () => {
    setActionLoading("run_now");
    try {
      const r = await fetch(`${API}/admin/email-automations/run-now`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { toast.success("Automation scheduler executed"); fetchAll(); }
      else { const e = await r.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed"); }
    finally { setActionLoading(false); }
  };

  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [viewingAutomation, setViewingAutomation] = useState(null);
  const [automationLogs, setAutomationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const viewAutomationLogs = async (automation) => {
    setViewingAutomation(automation);
    setLogsModalOpen(true);
    setLogsLoading(true);
    try {
      const r = await fetch(`${API}/admin/email-automations/${automation.automation_id}/logs?limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setAutomationLogs(d.logs || []); }
    } catch (err) { console.error("Template fetch failed:", err); }
    finally { setLogsLoading(false); }
  };

  // ─── Test Email ───────────────────────────────────────────
  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !testEmailTemplate) return;
    setTestEmailSending(true);
    try {
      const r = await fetch(`${API}/admin/email-templates/test-email`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          to_email: testEmailAddress,
          subject: testEmailTemplate.subject || "Test Email",
          html_content: testEmailTemplate.html_content || null,
          blocks: testEmailTemplate.blocks || null,
        }),
      });
      if (r.ok) { const d = await r.json(); if (d.success) toast.success(`Test email sent to ${testEmailAddress}`); else toast.error(d.message); }
      else { const e = await r.json(); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Failed to send test email"); }
    finally { setTestEmailSending(false); }
  };

  // ─── Helpers ──────────────────────────────────────────────
  const openDetailModal = async (t) => { setSelectedTemplate(t); const f = await fetchFullTemplate(t.template_key); if (f) setFullTemplate(f); setDetailModalOpen(true); };
  const openEditModal = async (t) => {
    setSelectedTemplate(t);
    const f = await fetchFullTemplate(t.template_key);
    if (f) { setFullTemplate(f); setEditSubject(f.subject); setEditHtmlContent(f.html_content || f.default_html_content || ""); }
    else { setEditSubject(t.subject); setEditHtmlContent(""); }
    setEditMode("preview"); setEditModalOpen(true);
  };

  const openTestEmail = (template) => {
    setTestEmailTemplate({ subject: template.subject, html_content: template.html_content || fullTemplate?.html_content });
    setTestEmailOpen(true);
  };

  const openSendModal = (template) => {
    setSendTemplate(template);
    setSendToEmail("");
    const vars = {};
    (template.variables || []).forEach((v) => { vars[v] = ""; });
    setSendVariables(vars);
    setSendModalOpen(true);
  };

  const handleSendOneOff = async () => {
    if (!sendToEmail || !sendTemplate) return;
    setSendLoading(true);
    try {
      const r = await fetch(`${API}/admin/email-templates/send-one-off`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ template_key: sendTemplate.template_key, to_email: sendToEmail, variables: sendVariables }),
      });
      const d = await r.json();
      if (r.ok && d.success) { toast.success(`Email sent to ${sendToEmail}`); setSendModalOpen(false); }
      else toast.error(d.detail || d.message || "Failed to send");
    } catch { toast.error("Failed to send email"); }
    finally { setSendLoading(false); }
  };

  const renderPreview = (html) => {
    if (!html) return "<p style='color:#999;text-align:center;padding:40px 0;'>No preview available</p>";
    const sample = { applicant_name: "Jane Smith", user_name: "Jane Smith", client_name: "Acme Healthcare", name: "Jane Smith", email: "jane@example.com", temp_password: "TempPass123!", portal_url: "https://www.rnbconsultinggroup.com/portal", submission_id: "FF-2025-0042", payment_link: "#", reset_link: "#" };
    let rendered = html;
    for (const [k, val] of Object.entries(sample)) rendered = rendered.replace(new RegExp(`\\{${k}\\}`, "g"), val);
    return rendered;
  };

  const insertVariable = (v) => setEditHtmlContent((prev) => prev + `{${v}}`);

  const filteredTemplates = templates.filter((t) => {
    const q = searchQuery.toLowerCase();
    const matchSearch = t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q) || t.trigger?.toLowerCase().includes(q) || t.subject?.toLowerCase().includes(q);
    return matchSearch && (activeCategory === "all" || t.category === activeCategory);
  });

  const catCounts = { all: templates.length };
  templates.forEach((t) => { catCounts[t.category] = (catCounts[t.category] || 0) + 1; });

  const stats = { total: templates.length, active: templates.filter((t) => t.is_active).length, customized: templates.filter((t) => t.is_customized || t.is_custom).length, automations: automations.length };
  const catConfig = [
    { key: "all", label: "All", icon: Mail }, { key: "program_applications", label: "Programs", icon: FileText },
    { key: "client_portal", label: "Portal", icon: Users }, { key: "account", label: "Account", icon: Shield },
    { key: "admin", label: "Admin", icon: Send }, { key: "public", label: "Public", icon: Globe }, { key: "custom", label: "Custom", icon: Sparkles },
  ];

  const triggerCatIcons = { invoice: Receipt, application: FileText, client: Users, project: Briefcase, discount: Tag, recurring: Repeat, manual: Hand };

  const selectedTrigger = triggerTypes[newAutomation.trigger_type];

  return (
    <AdminLayout activeNav="email-templates">
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight" data-testid="email-templates-title">Email Templates</h1>
            <p className="text-sm text-gray-400 mt-1">Design, automate, and test all system emails</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setAutomationModalOpen(true)} className="text-xs h-9 gap-1.5" data-testid="create-automation-btn">
              <Workflow className="h-3.5 w-3.5" /> New Automation
            </Button>
            <Button size="sm" onClick={() => { resetCreate(); setCreateModalOpen(true); }} className="bg-rnb-navy hover:bg-rnb-navy/90 text-xs h-9 gap-1.5" data-testid="create-template-btn">
              <Plus className="h-3.5 w-3.5" /> New Template
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Templates" value={stats.total} gradient="from-rnb-navy to-rnb-blue" icon={Mail} />
          <StatCard label="Active" value={stats.active} gradient="from-emerald-500 to-rnb-teal" icon={ToggleLeft} />
          <StatCard label="Customized" value={stats.customized} gradient="from-amber-400 to-orange-500" icon={Edit} />
          <StatCard label="Automations" value={stats.automations} gradient="from-violet-500 to-purple-500" icon={Workflow} />
        </div>

        {/* Tab Toggle: Templates / Automations / Recent Sends */}
        <div className="flex items-center gap-1 bg-gray-100/80 rounded-xl p-1 w-fit mb-5" data-testid="main-tabs">
          <button onClick={() => setActiveTab("templates")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "templates" ? "bg-white text-rnb-navy shadow-sm" : "text-gray-500"}`} data-testid="tab-templates">Templates</button>
          <button onClick={() => setActiveTab("automations")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${activeTab === "automations" ? "bg-white text-rnb-navy shadow-sm" : "text-gray-500"}`} data-testid="tab-automations">
            <Workflow className="w-3.5 h-3.5" /> Automations {automations.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600">{automations.length}</span>}
          </button>
          <button onClick={() => { setActiveTab("recent"); fetchRecentSends(); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${activeTab === "recent" ? "bg-white text-rnb-navy shadow-sm" : "text-gray-500"}`} data-testid="tab-recent-sends">
            <Clock className="w-3.5 h-3.5" /> Recently Sent
          </button>
        </div>

        {/* ═══════════════════ TEMPLATES TAB ═══════════════════ */}
        {activeTab === "templates" && (
          <>
            {/* Category pills + search + view toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1">
                {catConfig.map((c) => <CategoryPill key={c.key} label={c.label} icon={c.icon} count={catCounts[c.key] || 0} active={activeCategory === c.key} onClick={() => setActiveCategory(c.key)} testId={`cat-${c.key}`} />)}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-xl w-48 text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rnb-blue/20" data-testid="search-templates" />
                </div>
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button onClick={() => setViewMode("grid")} className={`p-1.5 rounded-md transition-colors ${viewMode === "grid" ? "bg-white shadow-sm text-rnb-navy" : "text-gray-400"}`} data-testid="view-grid"><LayoutGrid className="w-4 h-4" /></button>
                  <button onClick={() => setViewMode("list")} className={`p-1.5 rounded-md transition-colors ${viewMode === "list" ? "bg-white shadow-sm text-rnb-navy" : "text-gray-400"}`} data-testid="view-list"><List className="w-4 h-4" /></button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" /></div>
            ) : filteredTemplates.length > 0 ? (
              viewMode === "grid" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
                  {filteredTemplates.map((t) => <TemplateCardGrid key={t.template_key} template={t} onView={openDetailModal} onEdit={openEditModal} onToggle={handleToggle} onDelete={handleDeleteTemplate} onSend={openSendModal} actionLoading={actionLoading} />)}
                </div>
              ) : (
                <div className="space-y-2" data-testid="templates-list">
                  {filteredTemplates.map((t) => <TemplateCardList key={t.template_key} template={t} onView={openDetailModal} onEdit={openEditModal} onToggle={handleToggle} onDelete={handleDeleteTemplate} onSend={openSendModal} actionLoading={actionLoading} />)}
                </div>
              )
            ) : (
              <div className="bg-white rounded-xl py-16 text-center">
                <Mail className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">{searchQuery ? "No templates match" : "No templates"}</p>
              </div>
            )}
          </>
        )}

        {/* ═══════════════════ AUTOMATIONS TAB ═══════════════════ */}
        {activeTab === "automations" && (
          <div>
            {/* Run Now button */}
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-gray-400">{automations.filter((a) => a.is_active).length} active of {automations.length} total automations</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleRunNow} disabled={actionLoading === "run_now"} className="text-xs h-8 gap-1.5" data-testid="run-now-btn">
                  {actionLoading === "run_now" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5 text-amber-500" />} Run Now
                </Button>
              </div>
            </div>

            {automations.length === 0 ? (
              <div className="bg-white rounded-xl py-16 text-center">
                <Workflow className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400 mb-4">No automations configured yet</p>
                <Button size="sm" onClick={() => setAutomationModalOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-xs gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Create Your First Automation
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="automations-list">
                {automations.map((a) => {
                  const TIcon = triggerCatIcons[a.trigger_category] || Zap;
                  const sentCount = a.stats?.sent || 0;
                  const failedCount = a.stats?.failed || 0;
                  const lastRun = a.stats?.last_run;
                  return (
                    <div key={a.automation_id} className={`bg-white rounded-xl p-5 hover:shadow-md transition-all ${!a.is_active ? "opacity-50" : ""}`} data-testid={`automation-${a.automation_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shrink-0">
                            <TIcon className="w-4.5 h-4.5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-rnb-navy">{a.name}</p>
                            <p className="text-xs text-gray-400">
                              {a.trigger_label}
                              {a.offset_value != null && <> &middot; {a.offset_value > 0 ? `+${a.offset_value}` : a.offset_value} {a.trigger_type?.includes("hours") ? "%" : "days"}</>}
                              {a.condition && <> &middot; {a.condition}</>}
                              &middot; Template: <span className="text-gray-500">{a.template_key}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {/* Stats */}
                          <div className="flex items-center gap-3 text-xs mr-2">
                            <span className="text-emerald-600 font-medium" title="Emails sent">{sentCount} sent</span>
                            {failedCount > 0 && <span className="text-red-500 font-medium" title="Failed">{failedCount} failed</span>}
                            {lastRun && <span className="text-gray-300" title="Last run">{new Date(lastRun).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => viewAutomationLogs(a)} className="h-8 w-8 p-0 text-gray-400 hover:text-rnb-navy" title="View logs" data-testid={`auto-logs-${a.automation_id}`}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Switch checked={a.is_active} onCheckedChange={() => handleToggleAutomation(a.automation_id)} data-testid={`auto-toggle-${a.automation_id}`} />
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAutomation(a.automation_id)} className="h-8 w-8 p-0 text-gray-300 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════ RECENTLY SENT TAB ═══════════════════ */}
        {activeTab === "recent" && (
          <div data-testid="recent-sends-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{recentSends.length} email{recentSends.length !== 1 ? "s" : ""} sent</p>
              <Button variant="outline" size="sm" onClick={fetchRecentSends} className="h-8 text-xs gap-1.5" data-testid="refresh-recent">
                <RefreshCw className={`h-3 w-3 ${recentLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {recentLoading && recentSends.length === 0 ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-rnb-teal" /></div>
            ) : recentSends.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <Send className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-400">No emails sent yet</p>
                <p className="text-xs text-gray-300 mt-1">Emails sent via automations or one-off sends will appear here.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="recent-sends-table">
                    <thead>
                      <tr className="bg-gray-50/80 text-left">
                        <th className="px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Recipient</th>
                        <th className="px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Template</th>
                        <th className="px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Source</th>
                        <th className="px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Sent By</th>
                        <th className="px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                        <th className="px-4 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {recentSends.map((log, i) => (
                        <tr key={log.entity_id || i} className="hover:bg-gray-50/50 transition-colors" data-testid={`send-row-${i}`}>
                          <td className="px-4 py-3">
                            <span className="text-rnb-navy font-medium text-xs">{log.to_email}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-600">{log.template_name || log.template_key || "—"}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={log.source === "one_off" ? "outline" : "secondary"} className="text-[10px]">
                              {log.source === "one_off" ? "One-Off" : "Automation"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500">{log.sent_by_name || "System"}</span>
                          </td>
                          <td className="px-4 py-3">
                            {log.success ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle className="w-3 h-3" /> Sent</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-red-500"><AlertCircle className="w-3 h-3" /> Failed</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-400">
                              {log.sent_at ? new Date(log.sent_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : log.sent_date || "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════ AUTOMATION LOGS MODAL ═══════════════════ */}
        <Dialog open={logsModalOpen} onOpenChange={setLogsModalOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rnb-navy"><FileText className="h-5 w-5 text-violet-500" /> Execution Logs</DialogTitle>
              <DialogDescription>{viewingAutomation?.name}</DialogDescription>
            </DialogHeader>
            {logsLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-rnb-blue" /></div>
            ) : automationLogs.length === 0 ? (
              <div className="py-10 text-center"><p className="text-sm text-gray-400">No executions yet</p></div>
            ) : (
              <div className="space-y-2 py-2" data-testid="automation-logs">
                {automationLogs.map((log, i) => (
                  <div key={log.title || log.name || log.label || i} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm text-rnb-navy truncate">{log.to_email}</p>
                      <p className="text-[11px] text-gray-400">{log.entity_id} &middot; {new Date(log.sent_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                    </div>
                    <Badge className={`text-[10px] shrink-0 ${log.success ? "bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-50" : "bg-red-50 text-red-500 border border-red-200 hover:bg-red-50"}`}>
                      {log.success ? "Sent" : "Failed"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ═══════════════════ DETAIL MODAL ═══════════════════ */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedTemplate && fullTemplate && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-rnb-navy"><Eye className="h-5 w-5 text-rnb-blue" /> {selectedTemplate.name}</DialogTitle>
                  <DialogDescription>{selectedTemplate.description}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-xl p-3"><p className="text-[11px] text-gray-400 mb-0.5">Trigger</p><p className="text-sm font-medium text-rnb-navy flex items-center gap-1"><Zap className="h-3 w-3 text-amber-400" /> {selectedTemplate.trigger}</p></div>
                    <div className="bg-slate-50 rounded-xl p-3"><p className="text-[11px] text-gray-400 mb-0.5">Category</p><p className="text-sm font-medium text-rnb-navy capitalize">{categoryLabels[selectedTemplate.category] || selectedTemplate.category}</p></div>
                    <div className="bg-slate-50 rounded-xl p-3"><p className="text-[11px] text-gray-400 mb-0.5">Status</p><Badge className={`text-[10px] ${fullTemplate.is_active ? "bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-50" : "bg-red-50 text-red-500 border border-red-200 hover:bg-red-50"}`}>{fullTemplate.is_active ? "Active" : "Disabled"}</Badge></div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3"><p className="text-[11px] text-gray-400 mb-0.5">Subject Line</p><p className="text-sm font-medium text-rnb-navy">{fullTemplate.subject}</p></div>
                  {fullTemplate.variables?.length > 0 && (
                    <div><p className="text-xs text-gray-400 flex items-center gap-1 mb-2"><Info className="h-3 w-3" /> Variables</p><div className="flex flex-wrap gap-1.5">{fullTemplate.variables.map((v) => <Badge key={v} variant="outline" className="font-mono text-[11px] bg-slate-50">{`{${v}}`}</Badge>)}</div></div>
                  )}
                  {fullTemplate.html_content && (
                    <div>
                      <div className="flex items-center gap-2 mb-2"><Mail className="h-3.5 w-3.5 text-gray-400" /><p className="text-xs font-medium text-gray-500">Email Preview</p></div>
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 text-xs text-gray-400">To: jane@example.com &middot; Subject: {fullTemplate.subject}</div>
                        <div className="bg-white p-6 max-h-96 overflow-y-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(fullTemplate.html_content)) }} data-testid="detail-preview-content" />
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex items-center gap-2">
                  {selectedTemplate.is_customized && <Button variant="outline" onClick={() => handleReset(selectedTemplate.template_key)} className="text-red-500 border-red-200 hover:bg-red-50 mr-auto"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>}
                  <Button variant="outline" onClick={() => { openTestEmail(selectedTemplate); }} className="gap-1.5" data-testid="send-test-email-btn"><SendHorizonal className="h-4 w-4" /> Test Email</Button>
                  <Button onClick={() => { setDetailModalOpen(false); openEditModal(selectedTemplate); }} className="bg-rnb-navy hover:bg-rnb-navy/90"><Edit className="h-4 w-4 mr-1" /> Edit</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ═══════════════════ EDIT MODAL ═══════════════════ */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
            {selectedTemplate && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-rnb-navy"><Edit className="h-5 w-5 text-rnb-blue" /> Edit: {selectedTemplate.name}</span>
                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                      <button onClick={() => setEditMode("preview")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${editMode === "preview" ? "bg-white shadow-sm text-rnb-navy" : "text-gray-400"}`}><Monitor className="h-3.5 w-3.5" /> Preview</button>
                      <button onClick={() => setEditMode("code")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${editMode === "code" ? "bg-white shadow-sm text-rnb-navy" : "text-gray-400"}`}><Code className="h-3.5 w-3.5" /> HTML</button>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto space-y-4 py-4">
                  <div><label className="text-xs font-medium text-gray-500 mb-1.5 block">Subject Line</label><Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} data-testid="edit-subject-input" /></div>
                  {variableRegistry && (
                    <div><p className="text-xs text-gray-400 mb-1.5">Insert variable:</p><div className="flex flex-wrap gap-1">
                      {Object.entries(variableRegistry).map(([, info]) => info.variables.map((v) => <button key={v.key} onClick={() => insertVariable(v.key)} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-gray-500 hover:bg-rnb-blue/10 hover:text-rnb-blue transition-colors" title={`${v.label} — ${v.sample}`}>{`{${v.key}}`}</button>))}
                    </div></div>
                  )}
                  {editMode === "code" ? (
                    <Textarea value={editHtmlContent} onChange={(e) => setEditHtmlContent(e.target.value)} placeholder="HTML content..." className="font-mono text-sm min-h-[400px]" data-testid="edit-html-input" />
                  ) : (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 text-xs text-gray-400">Subject: {editSubject}</div>
                      <div ref={previewRef} className="bg-white p-6 min-h-[400px] max-h-[500px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(editHtmlContent)) }} />
                    </div>
                  )}
                </div>
                <DialogFooter className="flex justify-between border-t pt-4">
                  <div className="flex gap-2">
                    {selectedTemplate.is_customized && <Button variant="outline" onClick={() => handleReset(selectedTemplate.template_key)} className="text-red-500 border-red-200 hover:bg-red-50"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>}
                    <Button variant="outline" onClick={() => openTestEmail({ subject: editSubject, html_content: editHtmlContent })} className="gap-1.5" data-testid="edit-test-email-btn"><SendHorizonal className="h-4 w-4" /> Test</Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveTemplate} disabled={actionLoading === "update"} className="bg-rnb-teal hover:bg-rnb-teal/90">
                      {actionLoading === "update" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}<Save className="h-4 w-4 mr-1" /> Save
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ═══════════════════ CREATE TEMPLATE MODAL ═══════════════════ */}
        <Dialog open={createModalOpen} onOpenChange={(open) => { setCreateModalOpen(open); if (!open) resetCreate(); }}>
          <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rnb-navy"><Sparkles className="h-5 w-5 text-pink-500" /> Create New Template</DialogTitle>
              <DialogDescription>
                {createStep === 1 && "Set up the template details."}
                {createStep === 2 && "Design the email using the visual block editor."}
                {createStep === 3 && "Review and preview before creating."}
              </DialogDescription>
            </DialogHeader>

            {/* Step indicators */}
            <div className="flex items-center gap-2 py-2">
              {[{ n: 1, l: "Details" }, { n: 2, l: "Design" }, { n: 3, l: "Review" }].map(({ n, l }) => (
                <button key={n} onClick={() => setCreateStep(n)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${createStep === n ? "bg-rnb-navy text-white" : createStep > n ? "bg-rnb-teal/10 text-rnb-teal" : "bg-gray-100 text-gray-400"}`} data-testid={`create-step-${n}`}>
                  <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold ${createStep === n ? "bg-white/20" : createStep > n ? "bg-rnb-teal/20" : "bg-gray-200"}`}>{n}</span>
                  {l}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              {/* Step 1: Details */}
              {createStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs text-gray-500">Template Key *</Label><Input value={newTemplate.template_key} onChange={(e) => setNewTemplate({ ...newTemplate, template_key: e.target.value })} placeholder="invoice_reminder_3day" data-testid="new-template-key" /><p className="text-[11px] text-gray-400 mt-1">Lowercase with underscores</p></div>
                    <div><Label className="text-xs text-gray-500">Display Name *</Label><Input value={newTemplate.name} onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })} placeholder="Invoice Reminder (3 Day)" data-testid="new-template-name" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs text-gray-500">Category</Label>
                      <select value={newTemplate.category} onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-rnb-navy focus:outline-none focus:ring-2 focus:ring-rnb-blue/20" data-testid="new-template-category">
                        <option value="custom">Custom Templates</option><option value="client_portal">Client Portal</option><option value="admin">Admin</option><option value="public">Public</option>
                      </select>
                    </div>
                    <div><Label className="text-xs text-gray-500">Trigger Description</Label><Input value={newTemplate.trigger} onChange={(e) => setNewTemplate({ ...newTemplate, trigger: e.target.value })} placeholder="3 days before invoice due date" data-testid="new-template-trigger" /></div>
                  </div>
                  <div><Label className="text-xs text-gray-500">Subject Line *</Label><Input value={newTemplate.subject} onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })} placeholder="Reminder: Invoice due in {days_until_due} days" data-testid="new-template-subject" /></div>
                  <div><Label className="text-xs text-gray-500">Description</Label><Textarea value={newTemplate.description} onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })} placeholder="When this email is sent and what it's for..." rows={2} data-testid="new-template-description" /></div>
                </div>
              )}

              {/* Step 2: Design with Block Editor */}
              {createStep === 2 && (
                <EmailBlockEditor
                  blocks={newBlocks}
                  onChange={setNewBlocks}
                  variableRegistry={variableRegistry}
                  onInsertVariable={(v) => {
                    // Insert variable into the last text/info_box block's content
                    if (newBlocks.length > 0) {
                      const lastIdx = newBlocks.length - 1;
                      const last = newBlocks[lastIdx];
                      if (last.type === "text" || last.type === "info_box") {
                        const updated = [...newBlocks];
                        updated[lastIdx] = { ...last, props: { ...last.props, content: (last.props.content || "") + `{${v}}` } };
                        setNewBlocks(updated);
                        return;
                      }
                    }
                    toast.info(`Variable {${v}} copied — paste into a text block`);
                  }}
                />
              )}

              {/* Step 3: Review & Preview */}
              {createStep === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4"><p className="text-[11px] text-gray-400 mb-0.5">Name</p><p className="text-sm font-medium text-rnb-navy">{newTemplate.name || "—"}</p></div>
                    <div className="bg-slate-50 rounded-xl p-4"><p className="text-[11px] text-gray-400 mb-0.5">Subject</p><p className="text-sm font-medium text-rnb-navy">{newTemplate.subject || "—"}</p></div>
                  </div>
                  {newBlocks.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-500">Email Preview ({newBlocks.length} blocks)</p>
                        <Button size="sm" variant="outline" onClick={handlePreviewBlocks} className="text-xs h-7 gap-1"><Eye className="h-3 w-3" /> Refresh Preview</Button>
                      </div>
                      {blockPreviewHtml ? (
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 text-xs text-gray-400">Preview with sample data</div>
                          <div className="bg-white p-4 max-h-[400px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockPreviewHtml) }} data-testid="create-preview-content" />
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center">
                          <p className="text-sm text-gray-400">Click "Refresh Preview" to see the email</p>
                        </div>
                      )}
                    </div>
                  )}
                  {newBlocks.length === 0 && !newTemplate.html_content && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
                      No email content designed yet. Go back to Step 2 to add content blocks, or the template will be created without visual content.
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="border-t pt-4">
              <div className="flex items-center justify-between w-full">
                <div>
                  {createStep > 1 && <Button variant="outline" onClick={() => setCreateStep(createStep - 1)} data-testid="create-back-btn">Back</Button>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                  {createStep < 3 ? (
                    <Button onClick={() => {
                      if (createStep === 1 && (!newTemplate.template_key || !newTemplate.name || !newTemplate.subject)) { toast.error("Fill in required fields"); return; }
                      if (createStep === 2) handlePreviewBlocks();
                      setCreateStep(createStep + 1);
                    }} className="bg-rnb-navy hover:bg-rnb-navy/90" data-testid="create-next-btn">Next</Button>
                  ) : (
                    <Button onClick={handleCreateTemplate} disabled={actionLoading === "create"} className="bg-rnb-teal hover:bg-rnb-teal/90" data-testid="create-submit-btn">
                      {actionLoading === "create" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}<Plus className="h-4 w-4 mr-1" /> Create Template
                    </Button>
                  )}
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══════════════════ AUTOMATION MODAL ═══════════════════ */}
        <Dialog open={automationModalOpen} onOpenChange={setAutomationModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rnb-navy"><Workflow className="h-5 w-5 text-violet-500" /> New Automation</DialogTitle>
              <DialogDescription>Set up an automated email based on a trigger event.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-xs text-gray-500">Automation Name *</Label><Input value={newAutomation.name} onChange={(e) => setNewAutomation({ ...newAutomation, name: e.target.value })} placeholder="Invoice Reminder - 3 Days Before Due" data-testid="auto-name" /></div>

              <div><Label className="text-xs text-gray-500">Trigger Type *</Label>
                <Select value={newAutomation.trigger_type} onValueChange={(v) => {
                  const t = triggerTypes[v];
                  setNewAutomation({ ...newAutomation, trigger_type: v, offset_value: t?.default_offset || null, condition: "" });
                }}>
                  <SelectTrigger data-testid="auto-trigger-select"><SelectValue placeholder="Select a trigger..." /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(triggerCategories).map(([cat, info]) => (
                      <div key={cat}>
                        <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase">{info.label}</div>
                        {info.triggers?.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTrigger && <p className="text-[11px] text-gray-400 mt-1">{selectedTrigger.description}</p>}
              </div>

              {selectedTrigger?.requires_offset && (
                <div><Label className="text-xs text-gray-500">{selectedTrigger.offset_label || "Offset Value"}</Label>
                  <Input type="number" value={newAutomation.offset_value ?? ""} onChange={(e) => setNewAutomation({ ...newAutomation, offset_value: parseInt(e.target.value) || 0 })} data-testid="auto-offset" />
                </div>
              )}

              {selectedTrigger?.conditions?.length > 0 && (
                <div><Label className="text-xs text-gray-500">Condition</Label>
                  <Select value={newAutomation.condition} onValueChange={(v) => setNewAutomation({ ...newAutomation, condition: v })}>
                    <SelectTrigger data-testid="auto-condition-select"><SelectValue placeholder="Select condition..." /></SelectTrigger>
                    <SelectContent>{selectedTrigger.conditions.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              <div><Label className="text-xs text-gray-500">Email Template *</Label>
                <Select value={newAutomation.template_key} onValueChange={(v) => setNewAutomation({ ...newAutomation, template_key: v })}>
                  <SelectTrigger data-testid="auto-template-select"><SelectValue placeholder="Select template..." /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.template_key} value={t.template_key}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div><Label className="text-xs text-gray-500">Recipient</Label>
                <Select value={newAutomation.recipient_type} onValueChange={(v) => setNewAutomation({ ...newAutomation, recipient_type: v })}>
                  <SelectTrigger data-testid="auto-recipient-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client (associated)</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="custom">Custom Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newAutomation.recipient_type === "custom" && (
                <Input value={newAutomation.custom_recipient} onChange={(e) => setNewAutomation({ ...newAutomation, custom_recipient: e.target.value })} placeholder="email@example.com" data-testid="auto-custom-email" />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAutomationModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateAutomation} disabled={actionLoading === "auto_create"} className="bg-violet-600 hover:bg-violet-700" data-testid="auto-create-btn">
                {actionLoading === "auto_create" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}<Workflow className="h-4 w-4 mr-1" /> Create Automation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══════════════════ TEST EMAIL MODAL ═══════════════════ */}
        <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rnb-navy"><SendHorizonal className="h-5 w-5 text-rnb-teal" /> Send Test Email</DialogTitle>
              <DialogDescription>Send a test email with sample data to verify formatting.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label className="text-xs text-gray-500">Recipient Email *</Label><Input type="email" value={testEmailAddress} onChange={(e) => setTestEmailAddress(e.target.value)} placeholder="your@email.com" data-testid="test-email-input" /></div>
              <p className="text-[11px] text-gray-400">Variables will be replaced with sample data (e.g. "Acme Healthcare", "$1,500.00")</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestEmailOpen(false)}>Cancel</Button>
              <Button onClick={handleSendTestEmail} disabled={testEmailSending || !testEmailAddress} className="bg-rnb-teal hover:bg-rnb-teal/90" data-testid="send-test-btn">
                {testEmailSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SendHorizonal className="h-4 w-4 mr-1" />} Send Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══════════════════ SEND ONE-OFF EMAIL MODAL ═══════════════════ */}
        <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rnb-navy"><Send className="h-5 w-5 text-rnb-teal" /> Send Email</DialogTitle>
              <DialogDescription>Send a one-off email using the <strong>{sendTemplate?.name}</strong> template.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs text-gray-500">Recipient Email *</Label>
                <Input type="email" value={sendToEmail} onChange={(e) => setSendToEmail(e.target.value)} placeholder="recipient@example.com" data-testid="send-oneoff-email-input" />
              </div>

              {sendTemplate?.variables?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Template Variables</Label>
                  <p className="text-[11px] text-gray-400">Fill in values to personalize the email. Known values will be auto-populated from the recipient&apos;s profile.</p>
                  {sendTemplate.variables.map((v) => (
                    <div key={v} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-32 shrink-0 font-mono bg-gray-50 px-2 py-1.5 rounded">{`{${v}}`}</span>
                      <Input
                        value={sendVariables[v] || ""}
                        onChange={(e) => setSendVariables({ ...sendVariables, [v]: e.target.value })}
                        placeholder={`Value for ${v}`}
                        className="text-sm"
                        data-testid={`send-var-${v}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSendOneOff} disabled={sendLoading || !sendToEmail} className="bg-rnb-teal hover:bg-rnb-teal/90" data-testid="send-oneoff-btn">
                {sendLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminEmailTemplates;
